import React from 'react';
import {
  Dialog,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { ContentOpportunityItemV2 } from '../../types/content-strategy-v2.type';
import PagePlanDeck from './PagePlanDeck';
import { useContentStrategyActions } from '../hooks/useContentStrategyActions';

interface OpportunityDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  opportunity: ContentOpportunityItemV2 | null;
  // Todo: Content Plan - Add to plan callback props
  onAddToPlan?: (opportunityId: string) => void;
  onIgnore: (opportunityId: string) => void;
  isAddingToPlan?: boolean;
}

const formatPriority = (priority?: string) => {
  if (!priority) return '';
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1).toLowerCase()} Priority`;
};

const formatIntent = (intent?: string) => {
  if (!intent) return '—';
  return `${intent.charAt(0).toUpperCase()}${intent.slice(1).toLowerCase()}`;
};

const formatTraffic = (traffic?: number) => {
  if (traffic === undefined) return '0/month';
  return `${traffic.toLocaleString()}/month`;
};

const getPriorityClasses = (priority?: string) => {
  const p = priority?.toLowerCase();
  if (p === 'high') return '!bg-[#FEE4E2] !text-[#D92D20] !font-medium';
  if (p === 'medium') return '!bg-[#FEF0C7] !text-[#B54708] !font-medium';
  if (p === 'low') return '!bg-[#D1FADF] !text-[#027A48] !font-medium';
  return '!bg-[#F2F4F7] !text-[#344054] !font-medium';
};

export const OpportunityDetailsDrawer: React.FC<OpportunityDetailsDrawerProps> = ({
  open,
  onClose,
  opportunity,
  onAddToPlan,
  onIgnore,
  isAddingToPlan = false
}) => {
  const navigate = useNavigate();
  const { generatePagePlanMutation, ignoreOpportunityMutation } = useContentStrategyActions();

  if (!opportunity) return null;

  const title = opportunity.suggested_title || opportunity.topic;
  const isPlanned = opportunity.status === 'planned';
  const actionType = opportunity.actions?.generate_content?.type || 'generatePagePlan';
  const prefill = opportunity.actions?.generate_content?.prefill;

  const isGeneratingPlan = generatePagePlanMutation.isPending;

  const handlePrimaryAction = () => {
    if (actionType === 'redirectToBlogAgent') {
      onClose();
      navigate('/orbit-ai-agent/blog-post-agent', {
        state: {
          prefill: prefill || {
            topic: opportunity.topic,
            target_keyword: opportunity.target_keyword,
            suggested_title: opportunity.suggested_title,
            brief: opportunity.brief
          }
        }
      });
    } else {
      generatePagePlanMutation.mutate({
        opportunityId: opportunity.id
      });
    }
  };

  const handleIgnore = () => {
    ignoreOpportunityMutation.mutate(
      { opportunityId: opportunity.id },
      {
        onSuccess: () => {
          onClose();
        }
      }
    );
  };

  const handleRegeneratePlan = () => {
    generatePagePlanMutation.mutate({
      opportunityId: opportunity.id,
      regenerate: true
    });
  };

  const hasPagePlan = Boolean(opportunity.page_plan);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh'
        }
      }}
    >
      <div className="p-6 relative flex flex-col max-h-[90vh]">
        {/* Close Button */}
        <div className="absolute top-4 right-4 z-10">
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#667085',
              '&:hover': { backgroundColor: '#F2F4F7' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Header */}
        <div className="pr-10 mb-4">
          <div className="flex items-center gap-2.5 flex-wrap mb-2">
            <Chip
              label={formatPriority(opportunity.priority)}
              size="small"
              className={getPriorityClasses(opportunity.priority)}
            />
            {/* Todo: Content Plan - In Plan status chip in details drawer */}
            {/*
            {isPlanned && (
              <Chip
                label="✓ In Plan"
                size="small"
                className="!bg-[#D1FADF] !text-[#027A48] !font-medium"
              />
            )}
            */}
            <Chip
              label={opportunity.content_type?.replace('_', ' ') || 'Page'}
              size="small"
              className="!bg-[#F2F4F7] !text-[#344054] !text-xs !font-medium capitalize"
            />
          </div>
          <h2 className="text-xl font-semibold text-[#1D2939] leading-snug m-0">
            {title}
          </h2>
          {opportunity.brief && (
            <p className="text-sm text-[#475467] leading-relaxed mt-2 mb-0 font-normal">
              {opportunity.brief}
            </p>
          )}
        </div>

        <Divider />

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-4 my-4">
          {/* Metrics Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6 p-4 bg-[#F9FAFB] rounded-lg border border-[#EAECF0] text-xs">
            <div>
              <span className="text-[#667085] block font-medium mb-1">Search Volume</span>
              <span className="font-semibold text-[#1D2939] text-sm">
                {opportunity.search_volume?.toLocaleString() || 0}/month
              </span>
            </div>
            <div>
              <span className="text-[#667085] block font-medium mb-1">Keyword Difficulty</span>
              <span className="font-semibold text-[#1D2939] text-sm">
                {opportunity.difficulty || 0}/100
              </span>
            </div>
            <div>
              <span className="text-[#667085] block font-medium mb-1">Search Intent</span>
              <span className="font-semibold text-[#1D2939] text-sm">
                {formatIntent(opportunity.search_intent)}
              </span>
            </div>
            <div>
              <span className="text-[#667085] block font-medium mb-1">Traffic Potential</span>
              <span className="font-semibold text-[#1D2939] text-sm">
                {formatTraffic(opportunity.estimated_traffic_potential)}
              </span>
            </div>
            <div>
              <span className="text-[#667085] block font-medium mb-1">Target Keyword</span>
              <span className="font-semibold text-[#1D2939] text-sm">
                {opportunity.target_keyword || opportunity.topic}
              </span>
            </div>
            <div>
              <span className="text-[#667085] block font-medium mb-1">Category</span>
              <span className="font-semibold text-[#6941C6] text-sm capitalize">
                {opportunity.opportunity_category?.replace('_', ' ') || 'Strategic Gap'}
              </span>
            </div>
          </div>

          {/* AI Strategic Analysis */}
          <div className="p-4 bg-white border border-[#EAECF0] rounded-lg space-y-4">
            <div>
              <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                Why AI Suggested This
              </h4>
              <p className="text-sm text-[#344054] leading-relaxed m-0 font-normal">
                {opportunity.details?.why_ai_suggested_this ||
                  'Identified high potential keyword with strong topical relevance to your domain profile.'}
              </p>
            </div>

            {opportunity.details?.competitors_ranking &&
              opportunity.details.competitors_ranking.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                    Competitors Currently Ranking
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {opportunity.details.competitors_ranking.map((comp, idx) => (
                      <Chip
                        key={idx}
                        label={comp}
                        size="small"
                        className="!bg-[#F2F4F7] !text-[#344054] !text-xs !font-medium"
                      />
                    ))}
                  </div>
                </div>
              )}
          </div>

          {/* Generating Loader */}
          {isGeneratingPlan && (
            <div className="flex flex-col items-center justify-center p-8 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]">
              <CircularProgress size={32} sx={{ color: '#120242' }} />
              <p className="font-semibold text-sm text-[#1D2939] mt-4 mb-1">
                Orbit AI is generating page plan...
              </p>
              <p className="text-xs text-[#667085] max-w-sm text-center font-normal">
                Structuring headlines, section flow, and internal link suggestions.
              </p>
            </div>
          )}

          {/* Page Plan Deck if Available */}
          {hasPagePlan && !isGeneratingPlan && (
            <PagePlanDeck
              pagePlan={opportunity.page_plan!}
              onRegenerate={handleRegeneratePlan}
              isRegenerating={isGeneratingPlan}
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-[#EAECF0] mt-4 gap-3 flex-wrap">
          <Button
            variant="outlined"
            onClick={handleIgnore}
            disabled={ignoreOpportunityMutation.isPending}
            sx={{
              height: '40px',
              px: 3,
              borderColor: '#D92D20',
              color: '#D92D20',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#B42318',
                backgroundColor: 'rgba(217, 45, 32, 0.04)'
              }
            }}
          >
            {ignoreOpportunityMutation.isPending ? 'Ignoring...' : 'Ignore Suggestion'}
          </Button>

          <div className="flex items-center gap-3">
            {/* Todo: Content Plan - Add to Content Plan action button in details drawer */}
            {/*
            {!isPlanned ? (
              <Button
                variant="outlined"
                onClick={() => onAddToPlan && onAddToPlan(opportunity.id)}
                disabled={isAddingToPlan}
                sx={{
                  height: '40px',
                  px: 3,
                  borderColor: '#120242',
                  color: '#120242',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#0d0130',
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                {isAddingToPlan ? 'Adding...' : 'Add to Content Plan'}
              </Button>
            ) : (
              <Chip
                label="✓ In Content Plan"
                className="!bg-[#D1FADF] !text-[#027A48] !font-medium"
              />
            )}
            */}

            {!hasPagePlan && (
              <Button
                variant="contained"
                onClick={handlePrimaryAction}
                disabled={isGeneratingPlan}
                sx={{
                  height: '40px',
                  px: 3,
                  backgroundColor: '#120242',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#0d0130'
                  }
                }}
              >
                {actionType === 'redirectToBlogAgent'
                  ? 'Generate Blog Post'
                  : 'Generate Page Plan'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default OpportunityDetailsDrawer;
